import React, { ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { PifDoneAction, PifDoneRow, PifDoneStep, PifDoneTone } from "../done/PifDoneScreen";
import { APPROVAL_ROLES } from "../../../../services/pifService";
import { PIF_PDF_MESSAGES } from "../../../../constants/messages";
import { PifApprovalOutcome, PifApproverRole } from "../types";

interface ApprovalDoneContent {
  icon: ReactNode;
  tone: PifDoneTone;
  title: string;
  lede: string;
  receipt: PifDoneRow[];
  nextSteps: PifDoneStep[];
}

const OUTCOME_ICON: Record<PifApprovalOutcome, ReactNode> = {
  signed: <CheckIcon />,
  declined: <CloseIcon />,
  changes: <UndoIcon />,
};

const OUTCOME_TONE: Record<PifApprovalOutcome, PifDoneTone> = {
  signed: "success",
  declined: "warning",
  changes: "info",
};

export const buildApprovalDoneContent = (
  pifId: string,
  role: PifApproverRole,
  outcome: PifApprovalOutcome,
  signedAt: string,
): ApprovalDoneContent => {
  const roleMeta = APPROVAL_ROLES[role];
  const isFinal = outcome === "signed" && role === "exec";

  const title = outcome === "signed" ? (isFinal ? "The hire has been approved." : "Signed.") : outcome === "declined" ? "Declined." : "Sent back.";
  const lede =
    outcome === "signed"
      ? isFinal
        ? "All four signatures are in and the record is locked."
        : `Your signature is recorded and the form moves to ${roleMeta.forward}.`
      : outcome === "declined"
        ? "The request is closed and the supervisor has been notified."
        : "The supervisor can correct the form and resubmit it.";

  const receipt: PifDoneRow[] =
    outcome === "signed"
      ? [
          { k: "Reference", v: pifId },
          { k: "Action", v: isFinal ? "Final approval" : "Signed and forwarded" },
          { k: "Signed by", v: roleMeta.label },
          { k: "Timestamp", v: signedAt },
          { k: "Next", v: roleMeta.receiptNext },
        ]
      : [
          { k: "Reference", v: pifId },
          { k: "Action", v: outcome === "declined" ? "Declined" : "Sent back for changes" },
          { k: "By", v: roleMeta.label },
          { k: "Timestamp", v: signedAt },
        ];

  // Epic 82837: written for the approver reading it. A director or the Executive Director is never
  // promised a final copy (no PDF is generated or sent today) — only the supervisor's own screens
  // say a completed copy comes back.
  const nextSteps: PifDoneStep[] =
    outcome === "signed"
      ? isFinal
        ? [
            { title: "The hire has been approved", body: "All four signatures are in. The record is locked." },
            { title: "Onboarding opens", body: "HR creates the payroll and systems record." },
          ]
        : [
            { title: "Your signature is recorded", body: "Bound to this version of the form." },
            { title: `Moves to ${roleMeta.forward}`, body: "A signing link goes out within a minute." },
          ]
      : outcome === "declined"
        ? [
            { title: "The request is closed", body: "Nobody further in the chain is asked to sign." },
            { title: "The supervisor is notified", body: "They receive your reason in full." },
            { title: "Nothing reaches payroll", body: "No employee record is created for this hire." },
          ]
        : [
            { title: "Sent back to the supervisor", body: "They can edit and resubmit." },
            { title: "Signatures reset", body: "Any signature already given on this version is voided." },
            { title: "It comes back to you", body: "You'll get a fresh link once it's resubmitted." },
          ];

  return { icon: OUTCOME_ICON[outcome], tone: OUTCOME_TONE[outcome], title, lede, receipt, nextSteps };
};

interface ApprovalDoneHandlers {
  onCopyLink: () => void;
  onDownloadPdf: () => void;
  onBackToQueue: () => void;
  /** Epic 83470: true while the PDF download is in flight - relabels and disables that button. */
  pdfDownloading?: boolean;
}

/**
 * The approvers' done-screen buttons, the same for Approved, Sent Back and Declined. Built here
 * rather than inline in PostIFApproval so it can be unit-tested: that page needs the router, which
 * CRA's Jest can't resolve.
 *
 * Bug 83237: "Copy record link" was missing - the supervisor's done screen had it, the approvers'
 * did not. An emailed approver who has just signed has no list to go back to (no portal menu, so
 * the button would land on a no-access page), so "Back to my list" stays in-portal only. The label
 * is Epic 82838's one name for the way back from every PostIF screen.
 */
export const buildApprovalDoneActions = (emailedLink: boolean, handlers: ApprovalDoneHandlers): PifDoneAction[] => [
  { label: "Copy record link", variant: "outlined", onClick: handlers.onCopyLink, testId: "postif-approval-copylink" },
  {
    label: handlers.pdfDownloading ? PIF_PDF_MESSAGES.PREPARING : "Download PDF",
    variant: "outlined",
    onClick: handlers.onDownloadPdf,
    testId: "postif-approval-downloadpdf",
    disabled: handlers.pdfDownloading ?? false,
  },
  ...(emailedLink
    ? []
    : [{ label: "Back to my list", variant: "contained" as const, onClick: handlers.onBackToQueue, testId: "postif-approval-backtoqueue" }]),
];
